import React, { useState, useEffect } from "react";
import { Grid, Box, Button, Typography, CircularProgress } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InboundHeader from "../../molecules/Inventory/InboundHeader/index.tsx";
import InboundItems from "../../molecules/Inventory/InboundItems/index.tsx";
import { useCompany } from "../../contexts/CompanyContext";
import { createInbound, getInboundById, updateInbound } from "../../api/inbounds/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import { getPOItemList } from "../../api/purchaseOrders";
import { getProductById } from "../../api/products";
import dayjs from "dayjs";

interface InboundFormProps {
  onSubmit: () => void;
  id?: number;
}

const validationSchema = Yup.object().shape({
  inbound: Yup.object().shape({
    companyId: Yup.number().required("La empresa es requerida"),
    purchaseOrderId: Yup.number().nullable(),
    description: Yup.string(),
    warehouseId: Yup.number().required("El almacén es requerido"),
    inboundDate: Yup.date().nullable(),
  }),
  inboundItems: Yup.array().of(
    Yup.object().shape({
      poItemId: Yup.number().nullable(),
      description: Yup.string(),
      deliveryNo: Yup.number().required("El número de entrega es requerido"),
      quantityDelivered: Yup.number()
        .required("La cantidad es requerida")
        .min(1, "La cantidad debe ser mayor que 0"),
      weight: Yup.number()
        .required("El peso es requerido")
        .min(1, "El peso debe ser mayor que 0"),
      productId: Yup.number().nullable(),
      unitId: Yup.number().nullable(),
    })
  ),
});

const InboundForm: React.FC<InboundFormProps> = ({ onSubmit, id }) => {
  const formMode = id ? "edit" : "insert";
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<any>({
    inbound: {
      companyId: null,
      purchaseOrderId: null,
      description: '',
      warehouseId: null,
      inboundDate: null,
    },
    inboundItems: [],
  });
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getInboundById(id);
      const data = response.data;
      setFormData({
        inbound: {
          companyId: data.companyId,
          purchaseOrderId: data.purchaseOrderId,
          description: data.description,
          warehouseId: data.warehouseId,
          inboundDate: data.inboundDate ? dayjs(data.inboundDate) : null,
        },
        inboundItems: data.inboundItems?.map((item: any) => ({
          poItemId: item.poItemId,
          description: item.description,
          deliveryNo: item.deliveryNo,
          quantityDelivered: item.quantityDelivered,
          weight: item.weight,
          productId: item.productId,
          productCode: item.product?.code || "",
          unitId: item.unitId,
          workOrder: item.workOrder || "",
          orderQuantity: item.poItem?.quantity || 0,
        })) || [],
      });
      setSelectedPurchaseOrder(data.purchaseOrderId);
    } catch (error) {
      console.log(error);
      openSnack("Error al cargar la orden de ingreso", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        inbound: {
          companyId: selectedCompany.id,
          purchaseOrderId: null,
          description: '',
          warehouseId: null,
          inboundDate: null,
        },
        inboundItems: [],
      });
      setLoading(false);
    }
  }, [id, selectedCompany.id, formMode]);

  const createPayload = (values: any) => {
    const formatDate = (date: any) => (date ? dayjs(date).format("YYYY-MM-DD") : null);
    return {
      inbound: {
        ...values.inbound,
        companyId: selectedCompany.id,
        inboundDate: formatDate(values.inbound.inboundDate),
      },
      inboundItems: values.inboundItems,
    };
  };

  const handleSubmit = async (values: any) => {
    const payload = createPayload(values);
    try {
      if (id) {
        await updateInbound(id, payload);
        openSnack("Orden de ingreso actualizada correctamente", "success");
      } else {
        await createInbound(payload);
        openSnack("Orden de ingreso creada correctamente", "success");
      }
      onSubmit();
    } catch (error) {
      openSnack("Error al guardar la orden de ingreso", "error");
    }
  };

  const handlePurchaseOrderChange = (poId: number) => {
    setSelectedPurchaseOrder(poId);
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors }) => {
        const handleLoadPOItems = async () => {
          if (!selectedPurchaseOrder) {
            openSnack("Por favor seleccione una Orden de Compra", "warning");
            return;
          }

          try {
            const response = await getPOItemList({ purchaseOrderId: selectedPurchaseOrder });
            const poItems = response.data;

            if (!poItems || poItems.length === 0) {
              openSnack("La Orden de Compra no tiene productos", "info");
              return;
            }

            // Fetch product codes for all unique productIds
            const productIds: number[] = poItems
              .map((item: any) => item.productId as number | null | undefined)
              .filter((id): id is number => Boolean(id));
            const uniqueProductIds: number[] = [...new Set(productIds)];
            const productCodeMap: { [key: number]: string } = {};
            
            await Promise.all(
              uniqueProductIds.map(async (productId: number) => {
                try {
                  const productResponse = await getProductById(productId);
                  productCodeMap[productId] = productResponse.data.code || "";
                } catch (error) {
                  console.error(`Error fetching product ${productId}:`, error);
                  productCodeMap[productId] = "";
                }
              })
            );

            // Map PO items to inbound items format
            const mappedItems = poItems.map((poItem: any) => ({
              poItemId: poItem.id,
              description: poItem.concept || "",
              deliveryNo: 1,
              quantityDelivered: poItem.quantity,
              weight: 0,
              productId: poItem.productId || null,
              productCode: poItem.productId ? productCodeMap[poItem.productId] || "" : "",
              unitId: poItem.unitId || null,
              workOrder: poItem.workOrderId || "",
              orderQuantity: poItem.quantity || 0,
            }));

            // Update formik values
            setFieldValue("inboundItems", mappedItems);
            openSnack("Productos cargados correctamente", "success");
          } catch (error) {
            console.error("Error loading PO items:", error);
            openSnack("Error al cargar los productos de la Orden de Compra", "error");
          }
        };

        return (
          <Form>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <InboundHeader
                    companyId={selectedCompany.id}
                    onPurchaseOrderChange={handlePurchaseOrderChange}
                    onLoadPOItems={handleLoadPOItems}
                  />
              </Grid>
              <Grid item xs={12} md={8}>
                <InboundItems
                  purchaseOrderId={selectedPurchaseOrder}
                  formMode={formMode}
                  companyId={selectedCompany.id}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      console.log(errors);
                    }}
                  >
                    {formMode === "edit" ? "Actualizar" : "Guardar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
        );
      }}
    </Formik>
  );
};

export default InboundForm;
